import { useAuth } from "../../context/AuthContext";
import { useAgentPermissions } from "../../context/AgentPermissionsContext";
import { usePilotTraining } from "../../context/PilotTrainingContext";
import { trainingProgress } from "./pilotTrainingSteps";

/**
 * First-use pilot-training prompt (MF §14B, PILOT-TECHNICIAN-TRAINING-001).
 *
 * Offered once, on first sign-in, and never again after the technician starts,
 * pauses or dismisses it — relaunching stays available from Help. It is
 * deliberately non-blocking: a corner card with no backdrop, no focus capture,
 * and no route gating, so it can never stand between a technician and a ticket.
 * It renders nothing for a user with no steps they are permitted to perform.
 */
export default function PilotTrainingLauncher() {
  const training = usePilotTraining();
  const { user } = useAuth() || {};
  const { can: canAgent } = useAgentPermissions() || {};
  if (!training || !training.shouldOffer || training.open) return null;

  const role = user?.role;
  const isAdmin = role === "admin" || role === "owner";
  const { total, allStepsDone } = trainingProgress(training.progress, { isAdmin, canAgent, role });
  if (total === 0 || allStepsDone) return null;

  return (
    <aside
      dir="rtl"
      aria-label="הדרכת טכנאי פיילוט"
      data-testid="pilot-training-launcher"
      className="fixed bottom-4 right-4 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-purple-200 bg-white shadow-lg p-3 text-xs leading-relaxed text-gray-700"
    >
      <p className="font-semibold text-sm text-gray-900 mb-1">הדרכת טכנאי פיילוט</p>
      <p className="mb-2">
        סקירה קצרה של מסלול העבודה — מהקריאה ועד אימות התוצאה, כולל איך מזהים מתי המסך מציג ראיה
        מאומתת ומתי לא. אפשר לעצור ולחזור אליה בכל שלב.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={training.start}
          className="rounded bg-purple-600 px-3 py-1 text-xs text-white hover:bg-purple-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
        >
          התחל הדרכה
        </button>
        <button
          type="button"
          onClick={training.pause}
          className="text-gray-600 hover:text-gray-900 underline"
        >
          אחר כך
        </button>
        <button
          type="button"
          onClick={training.dismiss}
          className="text-gray-500 hover:text-gray-800 underline"
        >
          אל תציג שוב
        </button>
      </div>
    </aside>
  );
}
